import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PersonalizationPage from './pages/PersonalizationPage';
import ConversationPage from './pages/ConversationPage';
import ConversationPageRealtime from './pages/ConversationPageRealtime';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import SurveyPage from './pages/SurveyPage';
import MemoryEnrichmentPage from './pages/MemoryEnrichmentPage';
import ElderlyHomePage from './pages/ElderlyHomePage';
import './App.css';

// Detect API URL based on where frontend is accessed from
const getApiUrl = () => {
  // Use environment variable if set
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Get the hostname and protocol from the current page
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;
  
  // Debug logging
  console.log('🔍 URL Detection:', {
    hostname,
    protocol,
    port,
    fullUrl: window.location.href
  });
  
  // If accessing via HTTPS, use HTTPS for API (through Caddy proxy)
  if (protocol === 'https:') {
    // When behind Caddy, API is at the same hostname with /api path
    const apiUrl = `${protocol}//${hostname}/api`;
    console.log('✅ Using HTTPS API URL:', apiUrl);
    return apiUrl;
  }
  
  // For localhost HTTP access, use direct port
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const apiUrl = `${protocol}//${hostname}:8004`;
    console.log('✅ Using localhost API URL:', apiUrl);
    return apiUrl;
  }
  
  // Default: use same protocol and hostname, port 8004
  const apiUrl = `${protocol}//${hostname}:8004`;
  console.log('⚠️ Using default API URL:', apiUrl);
  return apiUrl;
};

const getWsUrl = () => {
  // Use environment variable if set
  if (process.env.REACT_APP_WS_URL) {
    return process.env.REACT_APP_WS_URL;
  }
  
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;
  
  // If accessing via HTTPS, use WSS for WebSocket (through Caddy proxy)
  if (protocol === 'https:') {
    // When behind Caddy, WebSocket is at the same hostname with /ws path
    const wsUrl = `wss://${hostname}/ws`;
    console.log('✅ Using WSS WebSocket URL:', wsUrl);
    return wsUrl;
  }
  
  // For localhost HTTP access, use direct port
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const wsUrl = `ws://${hostname}:8004`;
    console.log('✅ Using localhost WebSocket URL:', wsUrl);
    return wsUrl;
  }
  
  // Default: use WS protocol
  const wsUrl = `ws://${hostname}:8004`;
  console.log('⚠️ Using default WebSocket URL:', wsUrl);
  return wsUrl;
};

const API_URL = getApiUrl();
const WS_URL = getWsUrl();

// Debug: log the URLs (remove in production)
console.log('🌐 Final API URL:', API_URL);
console.log('🌐 Final WebSocket URL:', WS_URL);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <div>Loading...</div>
    </div>;
  }
  
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

function App() {
  return (
    <AuthProvider apiUrl={API_URL}>
      <Router>
        <div className="App">
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  <PersonalizationPage apiUrl={API_URL} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/personalization" 
              element={
                <ProtectedRoute>
                  <PersonalizationPage apiUrl={API_URL} />
                </ProtectedRoute>
              } 
            />
            {/* Survey Interview Page */}
            <Route 
              path="/survey/:profileId" 
              element={
                <ProtectedRoute>
                  <SurveyPage apiUrl={API_URL} wsUrl={WS_URL} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/enrich/:profileId" 
              element={
                <ProtectedRoute>
                  <MemoryEnrichmentPage apiUrl={API_URL} />
                </ProtectedRoute>
              } 
            />
            {/* Elderly Simple Home Page */}
            <Route 
              path="/home" 
              element={
                <ProtectedRoute>
                  <ElderlyHomePage apiUrl={API_URL} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/conversation/:profileId" 
              element={
                <ProtectedRoute>
                  <ConversationPage apiUrl={API_URL} wsUrl={WS_URL} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/conversation-realtime/:profileId" 
              element={
                <ProtectedRoute>
                  <ConversationPageRealtime apiUrl={API_URL} />
                </ProtectedRoute>
              } 
            />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;



